import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const { token } = useParams();
  const { acceptToken } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [error, setError] = useState("");
  // Refs hold the mutable dependencies so the verification effect can stay
  // token-scoped (running exactly once per URL change) without triggering
  // exhaustive-deps warnings or re-running when the auth or navigate ref
  // identity changes on re-render.
  const acceptTokenRef = useRef(acceptToken);
  const navigateRef = useRef(navigate);
  acceptTokenRef.current = acceptToken;
  navigateRef.current = navigate;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.post("/auth/verify-email", { token });
        if (!alive) return;
        if (r.data?.token && r.data?.user) {
          acceptTokenRef.current(r.data.token, r.data.user);
        }
        // Fire Google Ads "Sign Up" conversion exactly once per token. The
        // verify endpoint is single-use server-side, but a user could refresh
        // the success page; sessionStorage prevents double-counting.
        try {
          const fired = sessionStorage.getItem(`ads_signup_${token}`);
          if (!fired && typeof window.gtag === "function") {
            window.gtag("event", "conversion", {
              send_to: "AW-809141827/axuHCIDdlKYcEMOM6oED",
              transaction_id: token,
            });
            sessionStorage.setItem(`ads_signup_${token}`, "1");
          }
        } catch { /* analytics must never break the user flow */ }
        setState("ok");
        setTimeout(() => navigateRef.current("/dashboard", { replace: true }), 1800);
      } catch (e) {
        if (!alive) return;
        setError(apiError(e, "Verification failed"));
        setState("error");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="verify-email-page">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">[EMAIL CONFIRMATION]</div>
      {state === "verifying" && (
        <>
          <Loader2 size={48} className="mx-auto mb-6 animate-spin text-[#FF4F00]" />
          <h1 className="font-display font-bold text-3xl">Verifying your email…</h1>
        </>
      )}
      {state === "ok" && (
        <>
          <CheckCircle2 size={48} className="mx-auto mb-6 text-[#00A36C]" data-testid="verify-success-icon" />
          <h1 className="font-display font-bold text-4xl mb-2">Confirmed</h1>
          <p className="text-[#666]">Signing you in…</p>
        </>
      )}
      {state === "error" && (
        <>
          <AlertCircle size={48} className="mx-auto mb-6 text-[#FF4F00]" data-testid="verify-error-icon" />
          <h1 className="font-display font-bold text-3xl mb-2">Verification failed</h1>
          <p className="text-[#666] mb-6">{error}</p>
          <Link to="/register" className="cal-btn" data-testid="verify-error-register-link">Register again →</Link>
        </>
      )}
    </div>
  );
}
